/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import { observer } from 'mobx-react';
import React from 'react';
import './layout.scss';
import uuid from 'uuid';
import BannerCTA from '../../components/banner/bannerCTA';
import CCXUIAppConfig from '../../stores/config';

let videoAutoPlayed;

const renderLayout = ({
    textList,
    buttonList,
    videoList,
    textColor,
    onAction,
    analytics,
    bannerSectionId,
    bannerPlatform,
    name,
    appConfig,
    appsSkeletonModel,
    renderGuid,
}) => {
    const { bannerAnalyticsCallback } = analytics;
    if (typeof buttonList !== 'undefined' && buttonList && buttonList.length > 0) {
        bannerAnalyticsCallback('bannerRendered',
            {
                analytics,
	            CTAText: buttonList[0].label,
	            ingestId: buttonList[0].action.ingestId,
	            renderGuid,
	            isVideo: true,
                name,
            }, appConfig, appsSkeletonModel);
    }
    const contentArray = [];
    const style = { color: textColor };
    let videoSourceList = [];
    const supportedFormats = ['ogg', 'webm', 'mp4'];
    videoList.forEach((video) => {
        videoSourceList = [];
	    const gradientStyle = {
            /* stylelint-disable */
            backgroundImage: `linear-gradient(90deg, ${video.gradient || 'rgba(0,0,0,0.75)'}, hsla(0, 0%, 100%, 0))`,
            /* stylelint-enable */
        };
        supportedFormats.forEach((format) => {
            if (Object.prototype.hasOwnProperty.call(video, format)) {
                videoSourceList.push({
                    type: `video/${format}`,
                    src: video[format],
                });
            }
        });
	    const imageUrl = video.thumbnailImage
		                 && video.thumbnailImage.file
		                 && video.thumbnailImage.file.url;

	    contentArray.push(
			<>
            <div className={CCXUIAppConfig.isNonDownloadableSurface ? 'banner-background-video no-video' : 'banner-background-video'}>
			    <video
			        className="banner-video"
			        autoPlay
			        loop
			        muted
			        poster={imageUrl}
			        style={{ backgroundImage: `url(${imageUrl})` }}
			        playsInline
			        onPause={() => { bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'pause' }, appConfig, appsSkeletonModel); }}
			        onPlay={
			            () => {
			                if (!videoAutoPlayed) {
			                    bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'resume' }, appConfig, appsSkeletonModel);
			                } else {
			                    videoAutoPlayed = false;
			                }
			            }
			        }>
			        {videoSourceList.map(item => <source src={item.src} type={item.type} />)}
			    </video>
            </div>
			<div className="banner-overlay" style={gradientStyle} />
			</>
        );
    });


    if (buttonList && buttonList.length > 0) {
        const ele = (
            <div className="layout-content">
                {textList && textList.length > 0
                && (
                    <div>
                        <div className="title" style={style}>{textList[0]}</div>
                        <div className="desc" style={style}>{textList[1]}</div>
                    </div>
                )}
                {buttonList.map((buttonItem) => {
	                const guid = uuid.v4();
	                return (
                        <BannerCTA
                            buttonItem={buttonItem}
	                           onAction={() => onAction(buttonItem.action, guid,
		                           bannerSectionId, bannerPlatform)}
	                           style={style}
	                           textColor={textColor}
	                           bannerAnalyticsCallback={() => bannerAnalyticsCallback('bannerCTAClicked',
		                           {
			                           analytics: { ...analytics, guid, url: buttonItem.action.url },
			                           CTAText: buttonItem.label,
			                           ingestId: buttonItem.action.ingestId,
			                           name,
		                           }, appConfig, appsSkeletonModel)}

	                />
                    );
                })}
            </div>
        );
        contentArray.push(ele);
    }
    return contentArray;
};

export default observer(({
    bannerItem, bannerSectionId, bannerPlatform, appConfig, appsSkeletonModel,
}) => {
    const {
        textList,
        buttonList,
        textColor,
        videoList,
        analytics,
        name,
	    backgroundColor,
	    onAction,
    } = bannerItem;
    const style = { color: backgroundColor, backgroundColor };
    const renderGuid = uuid.v4();
    return (
        <div
            className="video-with-text-layout"
            render-guid={renderGuid}
            style={style}>
            {renderLayout({
                textList,
                buttonList,
                videoList,
                textColor,
	            onAction,
                analytics,
	            bannerSectionId,
                bannerPlatform,
                name,
                appConfig,
                appsSkeletonModel,
	            renderGuid,
            })}
        </div>
    );
});
